import type {
    BaseQueryFn,
    FetchArgs,
    FetchBaseQueryError,
} from '@reduxjs/toolkit/query/react';
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { selectAuth } from "../features/authSlice";
import { RootState } from "../store";
import { API_END_POINTS } from './apiEndpoints';
import { baseUrl } from './baseUrl';

const rawBaseQuery = fetchBaseQuery({
    baseUrl: baseUrl.MAIN_BASE_URL,
})

type ExtraOptions = {
    headers?: Record<string, string>;
    // Define other optional properties here if needed
};

const dynamicBaseQuery: BaseQueryFn<
    string | FetchArgs,
    unknown,
    FetchBaseQueryError,
    ExtraOptions
> = async (args, api, extraOptions) => {
    const orgBaseUrl = selectAuth(api.getState() as RootState).baseURL;
    // gracefully handle scenarios where data to generate the URL is missing
    if (!orgBaseUrl) {
        return {
            error: {
                status: 400,
                statusText: 'Bad Request',
                data: 'dynamic route not found',
            },
        }
    }

    const urlEnd = typeof args === 'string' ? args : args.url

    // construct a dynamically generated portion of the url
    const adjustedUrl = `${orgBaseUrl}/api/ExpenseViolation${urlEnd}`
    const adjustedArgs = typeof args === 'string' ? { url: adjustedUrl } : { ...args, url: adjustedUrl };
    // Get the token from localStorage
    const token = localStorage.getItem("user")
        ? JSON.parse(localStorage.getItem("user")!).token
        : null;
    // Set headers if token is available
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    // Merge headers with existing headers
    const mergedHeaders = { ...headers, ...extraOptions?.headers };
    // provide the amended url and other params to the raw base query
    return rawBaseQuery({ ...adjustedArgs, headers: mergedHeaders }, api, extraOptions)

}

export const ExpenseVoilationApi = createApi({
    reducerPath: 'ExpenseVoilationApi',
    baseQuery: dynamicBaseQuery,
    // baseQuery: fetchBaseQuery({
    //     baseUrl: baseUrl.BASE_URL + `/api/ExpenseViolation/`,
    //     prepareHeaders: (headers) => {
    //         // Get the token from localStorage
    //         const token = localStorage.getItem("user")
    //             ? JSON.parse(localStorage.getItem("user")!).token
    //             : null;
    //         if (token) {
    //             headers.set('Authorization', `Bearer ${token}`);
    //         }

    //         return headers;
    //     },
    // }),
    endpoints: (builder) => ({
        getAllViolationPolicyField: builder.query({
            query: ({ pageNumber, pageSize, searchStr, sortBy, sortOrder, filterByStatus }) => {
                return {
                    url: `${API_END_POINTS.getAllViolationPolicyField}?pageNumber=${pageNumber}&pageSize=${pageSize}&sortBy=${sortBy}&sortOrder=${sortOrder}&searchStr=${searchStr}&filterByStatus=${filterByStatus}`,
                    method: "GET",
                }
            },
        }),
        addViolationPolicyField: builder.mutation({
            query: (body) => {
                return {
                    url: API_END_POINTS.addViolationPolicyField,
                    method: "POST",
                    body,
                }
            },
        }),
        updateViolationPolicyField: builder.mutation({
            query: (body) => {
                return {
                    url: API_END_POINTS.updateViolationPolicyField,
                    method: "PUT",
                    body,
                }
            },
        }),
        getViolationPolicyFieldById: builder.query({
            query: ({ activeExpenseViolationId }) => {
                return {
                    url: `${API_END_POINTS.getViolationPolicyFieldById}?activeExpenseViolationId=${activeExpenseViolationId}}`,
                    method: "GET",
                }
            },
        }),
        getExpenseViolationsByExpenseId: builder.query({
            query: ({ expenseId }) => {
                return {
                    url: `${API_END_POINTS.getExpenseViolationsByExpenseId}?expenseId=${expenseId}}`,
                    method: "GET",
                }
            },
        }),
        changeViolationPolicyFieldStatus: builder.mutation({
            query: ({ activeExpenseViolationId, status }) => {
                return {
                    url: `${API_END_POINTS.changeViolationPolicyFieldStatus}?activeExpenseViolationId=${activeExpenseViolationId}&status=${status}`,
                    method: "PUT",
                }
            },
        }),
        upsertExpenseViolation: builder.mutation({
            query: (body) => ({
                url: API_END_POINTS.upsertExpenseViolation,
                method: 'PUT',
                body,
            }),
        }),
    }),
})


export const {
    useGetAllViolationPolicyFieldQuery,
    useAddViolationPolicyFieldMutation,
    useUpdateViolationPolicyFieldMutation,
    useGetViolationPolicyFieldByIdQuery,
    useGetExpenseViolationsByExpenseIdQuery,
    useChangeViolationPolicyFieldStatusMutation,
    useUpsertExpenseViolationMutation
} = ExpenseVoilationApi